/**
 * Admin API process health (/health/ready) for the operations overview. A 503 carries a degraded
 * readiness body; anything else that is not 2xx, or not the expected shape (e.g. a proxy error page),
 * is "unreachable" — never rendered as healthy.
 */
import { Button, Card, Skeleton, StateView, StatusBadge } from '@hopex/ui';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

type Ready = {
  status: 'ready' | 'degraded';
  service: string;
  checks: { database: 'ok' | 'unavailable'; migrations: 'current' | 'pending' | 'unknown' };
};
type Health =
  | { state: 'loading' }
  | { state: 'ok'; data: Ready; at: Date }
  | { state: 'unreachable'; at: Date };

async function probeAdminHealth(): Promise<Health> {
  try {
    const res = await fetch('/health/ready', { headers: { accept: 'application/json' } });
    if (!res.ok && res.status !== 503) return { state: 'unreachable', at: new Date() };
    const data = (await res.json()) as Partial<Ready>;
    if (
      (data.status !== 'ready' && data.status !== 'degraded') ||
      typeof data.checks !== 'object'
    ) {
      return { state: 'unreachable', at: new Date() };
    }
    return { state: 'ok', data: data as Ready, at: new Date() };
  } catch {
    return { state: 'unreachable', at: new Date() };
  }
}

export function SystemHealthCard() {
  const [health, setHealth] = useState<Health>({ state: 'loading' });
  useEffect(() => {
    let live = true;
    void probeAdminHealth().then((h) => live && setHealth(h));
    return () => {
      live = false;
    };
  }, []);
  const reload = useCallback(() => {
    setHealth({ state: 'loading' });
    void probeAdminHealth().then(setHealth);
  }, []);
  return (
    <Card className="p-4 sm:p-5" aria-labelledby="sys-health" data-system-health>
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id="sys-health" className="m-0 text-[14px] font-bold">
          سلامت سامانه (API مدیریت)
        </h2>
        <Button
          variant="ghost"
          size="sm"
          icon={<RefreshCw aria-hidden className="size-4" />}
          onPress={reload}
          loading={health.state === 'loading'}
        >
          بررسی دوباره
        </Button>
      </div>
      {health.state === 'loading' && (
        <div className="grid gap-3">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-6 w-2/3" />
        </div>
      )}
      {health.state === 'unreachable' && (
        <StateView
          compact
          kind="offline"
          title="API مدیریت در دسترس نیست"
          description="فرایند api-admin را اجرا کنید یا وضعیت شبکه را بررسی کنید."
        />
      )}
      {health.state === 'ok' && (
        <>
          <dl className="m-0 grid gap-2.5 text-[13px]">
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">وضعیت کلی</dt>
              <dd className="m-0">
                <StatusBadge
                  status={health.data.status === 'ready' ? 'completed' : 'rejected'}
                  label={health.data.status === 'ready' ? 'آماده' : 'ناقص'}
                />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">پایگاه داده</dt>
              <dd className="m-0">
                <StatusBadge
                  status={health.data.checks.database === 'ok' ? 'completed' : 'rejected'}
                  label={health.data.checks.database === 'ok' ? 'متصل' : 'قطع'}
                />
              </dd>
            </div>
            <div className="flex items-center justify-between gap-3">
              <dt className="text-fg-muted">مهاجرت‌ها</dt>
              <dd className="m-0">
                <StatusBadge
                  status={health.data.checks.migrations === 'current' ? 'completed' : 'hold'}
                  label={health.data.checks.migrations === 'current' ? 'به‌روز' : 'نیاز به اجرا'}
                />
              </dd>
            </div>
          </dl>
          <p className="m-0 mt-3 border-t border-line pt-3 text-xs text-fg-subtle">
            آخرین بررسی: <span className="hx-num">{health.at.toLocaleTimeString('en-GB')}</span>
          </p>
        </>
      )}
    </Card>
  );
}
